import { ConfigurationValues, JSONRecordData } from '../Log/graph.util';
import {
  Alert,
  Box,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Modal,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme
} from '@mui/material';

import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axiosConfig from '../../../Resources/axiosConfig';
import utc from 'dayjs/plugin/utc';
import { UserContext } from '../../../contexts/userContext';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TimePicker } from '@mui/lab';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';

interface ConfigurationProps {
  values: JSONRecordData;
  id: number;
}

function Configuration(props: ConfigurationProps) {
  if (props.values === null) {
    return null;
  }

  //console.log(props.values.Config);
  const [activeTab, setActiveTab] = useState<'charge' | 'discharge'>('charge');

  const CalibrationChargeOptions = [
    'Repetitive Calibration',
    'Additional Calibration',
    'Force Permanent Calibration Now'
  ];

  const CalibrationChargeOptionsController = [
    'RepetitivelyEvery',
    'AdditionallyOnce',
    'ChargePermanently'
  ];

  const daysInWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  dayjs.extend(utc);
  const localOffset = dayjs().utcOffset();

  const [errors, setErrors] = useState({
    minimumSoC: false,
    gridSetPoint: false
  });

  const SetErrorForField = (field_name, state) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field_name]: state
    }));
  };
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [dateSelectionError, setDateSelectionError] = useState('');
  const [isErrorDateModalOpen, setErrorDateModalOpen] = useState(false);
  const context = useContext(UserContext);
  const { currentUser, setUser } = context;
  const { product, setProduct } = useContext(ProductIdContext);

  const [formValues, setFormValues] = useState<Partial<ConfigurationValues>>({
    minimumSoC: props.values.Config.MinSoc,
    gridSetPoint: (props.values.Config.GridSetPoint as number) / 1000,
    calibrationChargeState: CalibrationChargeOptionsController.indexOf(
      props.values.Config.ForceCalibrationChargeState.toString()
    ),
    calibrationChargeDate:
      CalibrationChargeOptionsController.indexOf(
        props.values.Config.ForceCalibrationChargeState.toString()
      ) == 0
        ? dayjs(props.values.Config.DayAndTimeForRepetitiveCalibration)
            // .add(localOffset, 'minute')
            .toDate()
        : dayjs(props.values.Config.DayAndTimeForAdditionalCalibration)
            // .add(localOffset, 'minute')
            .toDate(),

    ...(product === 3 && {
      calibrationDischargeState: CalibrationChargeOptionsController.indexOf(
        props.values.Config.ForceCalibrationDischargeState.toString()
      ),
      calibrationDischargeDate:
        CalibrationChargeOptionsController.indexOf(
          props.values.Config.ForceCalibrationDischargeState.toString()
        ) == 0
          ? dayjs(
              props.values.Config.DownDayAndTimeForRepetitiveCalibration
            ).toDate()
          : dayjs(
              props.values.Config.DownDayAndTimeForAdditionalCalibration
            ).toDate()
    })
  });

  // console.log(formValues);

  const handleSubmit = async (e) => {
    if (
      CalibrationChargeOptionsController.indexOf(
        props.values.Config.ForceCalibrationChargeState.toString()
      ) != 2 &&
      props.values.EssControl.Mode === 'CalibrationCharge'
    ) {
      setDateSelectionError(
        'You cannot change the date while the installation is in Calibration Charge Mode'
      );
      setErrorDateModalOpen(true);
      return;
    } else if (
      formValues.calibrationChargeState === 1 &&
      dayjs(formValues.calibrationChargeDate).isBefore(dayjs())
    ) {
      //console.log('asked for', dayjs(formValues.calibrationChargeDate));
      setDateSelectionError('You must specify a future date');
      setErrorDateModalOpen(true);
      return;
    } else {
      // console.log('asked for', dayjs(formValues.calibrationChargeDate));
      const configurationToSend: Partial<ConfigurationValues> = {
        minimumSoC: formValues.minimumSoC,
        gridSetPoint: formValues.gridSetPoint,
        calibrationChargeState: formValues.calibrationChargeState,
        calibrationChargeDate: dayjs
          .utc(formValues.calibrationChargeDate)
          .add(localOffset, 'minute')
          .toDate(),
        calibrationDischargeState: formValues.calibrationDischargeState,
        calibrationDischargeDate: dayjs
          .utc(formValues.calibrationDischargeDate)
          .add(localOffset, 'minute')
          .toDate()
      };

      setLoading(true);
      const res = await axiosConfig
        .post(
          `/EditInstallationConfig?installationId=${props.id}&product=${product}`,
          configurationToSend
        )
        .catch((err) => {
          if (err.response) {
            setError(true);
            setLoading(false);
          }
        });

      if (res) {
        setUpdated(true);
        setLoading(false);
      }
    }
  };

  const handleOkOnErrorDateModal = () => {
    setErrorDateModalOpen(false);
  };

  const handleConfirm = (newDate) => {
    //console.log('non adapted day is ', newDate);
    //console.log('adapted day is ', dayjs.utc(newDate).toDate());
    setFormValues({
      ...formValues,
      ['calibrationChargeDate']: dayjs(newDate).toDate()
    });
  };

  const handleConfirmDischarge = (newDate) => {
    //console.log('non adapted day is ', newDate);
    //console.log('adapted day is ', dayjs.utc(newDate).toDate());
    setFormValues({
      ...formValues,
      ['calibrationDischargeDate']: dayjs(newDate).toDate()
    });
  };

  const handleSelectedCalibrationChargeDay = (event) => {
    const selectedDay = daysInWeek.indexOf(event.target.value);
    const currentDate = dayjs();
    let difference = selectedDay - currentDate.day();
    if (difference < 0) {
      difference += 7;
    }

    const adjustedDate = currentDate.add(difference, 'day');
    setFormValues({
      ...formValues,
      ['calibrationChargeDate']: adjustedDate.toDate()
    });
  };

  const handleSelectedCalibrationDisChargeDay = (event) => {
    const selectedDay = daysInWeek.indexOf(event.target.value);
    const currentDate = dayjs();
    let difference = selectedDay - currentDate.day();
    if (difference < 0) {
      difference += 7;
    }

    const adjustedDate = currentDate.add(difference, 'day');
    setFormValues({
      ...formValues,
      ['calibrationDischargeDate']: adjustedDate.toDate()
    });
  };

  const handleSelectedCalibrationChargeChange = (event) => {
    setFormValues({
      ...formValues,
      ['calibrationChargeState']: CalibrationChargeOptions.indexOf(
        event.target.value
      ),
      ['calibrationChargeDate']:
        CalibrationChargeOptions.indexOf(event.target.value) == 0
          ? dayjs(props.values.Config.DayAndTimeForRepetitiveCalibration)
              // .add(localOffset, 'minute')
              .toDate()
          : dayjs(props.values.Config.DayAndTimeForAdditionalCalibration)
              // .add(localOffset, 'minute')
              .toDate()
    });
  };

  const handleSelectedCalibrationDisChargeChange = (event) => {
    setFormValues({
      ...formValues,
      ['calibrationDischargeState']: CalibrationChargeOptions.indexOf(
        event.target.value
      ),
      ['calibrationDischargeDate']:
        CalibrationChargeOptions.indexOf(event.target.value) == 0
          ? dayjs(props.values.Config.DownDayAndTimeForRepetitiveCalibration)
              // .add(localOffset, 'minute')
              .toDate()
          : dayjs(props.values.Config.DownDayAndTimeForAdditionalCalibration)
              // .add(localOffset, 'minute')
              .toDate()
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case 'minimumSoC':
        if (
          /[^0-9.]/.test(value) ||
          isNaN(parseFloat(value)) ||
          parseFloat(value) > 100
        ) {
          SetErrorForField(name, true);
        } else {
          SetErrorForField(name, false);
        }
        break;
      case 'gridSetPoint':
        if (/[^0-9.]/.test(value) || isNaN(parseFloat(value))) {
          SetErrorForField(name, true);
        } else {
          SetErrorForField(name, false);
        }
        break;

      default:
        return true;
    }

    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  return (
    <Container maxWidth="xl">
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        {isErrorDateModalOpen && (
          <Modal open={isErrorDateModalOpen} onClose={() => {}}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 450,
                bgcolor: 'background.paper',
                borderRadius: 4,
                boxShadow: 24,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography
                variant="body1"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                {dateSelectionError}
              </Typography>

              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={handleOkOnErrorDateModal}
              >
                Ok
              </Button>
            </Box>
          </Modal>
        )}

        <Grid item xs={12} md={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 2,
              mb: 2
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              textColor="inherit"
              TabIndicatorProps={{ style: { display: 'none' } }} // hide default underline
              sx={{
                bgcolor: '#f5f5f5',
                borderRadius: 2,
                p: 0.5,
                boxShadow: 1,
                width: 500,
                height: 47
              }}
            >
              <Tab
                value="charge"
                label="Calibration Charge"
                sx={(theme) => ({
                  flex: 2,
                  fontWeight: 'bold',
                  borderRadius: 2,
                  textTransform: 'none',
                  color:
                    activeTab === 'charge'
                      ? 'white'
                      : theme.palette.text.primary,
                  bgcolor:
                    activeTab === 'charge'
                      ? theme.palette.primary.main
                      : 'transparent',

                  '&:hover': {
                    bgcolor:
                      activeTab === 'charge'
                        ? theme.palette.primary.dark
                        : '#eee'
                  }
                })}
              />
              {/*{product === 3 && (*/}
              {/*  <Tab*/}
              {/*    value="discharge"*/}
              {/*    label="Calibration Discharge"*/}
              {/*    sx={(theme) => ({*/}
              {/*      flex: 2,*/}
              {/*      fontWeight: 'bold',*/}
              {/*      borderRadius: 2,*/}
              {/*      textTransform: 'none',*/}
              {/*      color:*/}
              {/*        activeTab === 'discharge'*/}
              {/*          ? 'white'*/}
              {/*          : theme.palette.text.primary,*/}
              {/*      bgcolor:*/}
              {/*        activeTab === 'discharge'*/}
              {/*          ? theme.palette.primary.main*/}
              {/*          : 'transparent',*/}
              {/*      '&:hover': {*/}
              {/*        bgcolor:*/}
              {/*          activeTab === 'discharge'*/}
              {/*            ? theme.palette.primary.dark*/}
              {/*            : '#eee'*/}
              {/*      }*/}
              {/*    })}*/}
              {/*  />*/}
              {/*)}*/}
            </Tabs>
          </Box>

          <CardContent>
            <Box
              component="form"
              sx={{
                '& .MuiTextField-root': { m: 1, width: 390 }
              }}
              noValidate
              autoComplete="off"
            >
              {activeTab === 'charge' && (
                <>
                  <div style={{ marginBottom: '5px' }}>
                    <TextField
                      label={
                        <FormattedMessage
                          id="minimum_soc "
                          defaultMessage="Minimum SoC (%)"
                        />
                      }
                      name="minimumSoC"
                      value={formValues.minimumSoC}
                      onChange={handleChange}
                      helperText={
                        errors.minimumSoC ? (
                          <span style={{ color: 'red' }}>
                            Value should be between 0-100%
                          </span>
                        ) : (
                          ''
                        )
                      }
                      fullWidth
                    />
                  </div>

                  <div style={{ marginBottom: '5px', marginTop: '10px' }}>
                    <FormControl fullWidth sx={{ marginLeft: 1, width: 390 }}>
                      <InputLabel
                        sx={{
                          fontSize: 14,
                          backgroundColor: 'white'
                        }}
                      >
                        <FormattedMessage
                          id="forced_calibration_charge"
                          defaultMessage="Calibration Charge State"
                        />
                      </InputLabel>
                      <Select
                        value={
                          CalibrationChargeOptions[
                            formValues.calibrationChargeState
                          ]
                        }
                        onChange={handleSelectedCalibrationChargeChange}
                      >
                        {CalibrationChargeOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  {formValues.calibrationChargeState == 1 && (
                    <div>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          label="Select Next Calibration Charge Date"
                          value={dayjs(formValues.calibrationChargeDate)}
                          onChange={handleConfirm}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{
                                marginTop: 2, // Apply styles here
                                width: '100%' // Optional: You can adjust the width or other styling here
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  )}

                  {formValues.calibrationChargeState == 0 && (
                    <>
                      <div style={{ marginBottom: '5px' }}>
                        <FormControl
                          fullWidth
                          sx={{ marginLeft: 1, width: 390, marginTop: 2 }}
                        >
                          <InputLabel
                            sx={{
                              fontSize: 14,
                              backgroundColor: 'white'
                            }}
                          >
                            <FormattedMessage
                              id="calibration_charge_day"
                              defaultMessage="Calibration Charge Day"
                            />
                          </InputLabel>
                          <Select
                            value={
                              daysInWeek[
                                formValues.calibrationChargeDate.getDay()
                              ]
                            }
                            onChange={handleSelectedCalibrationChargeDay}
                          >
                            {daysInWeek.map((day) => (
                              <MenuItem key={day} value={day}>
                                {day}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>

                      <div style={{ marginBottom: '5px', marginTop: '10px' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            ampm={false}
                            label="Calibration Charge Hour"
                            value={dayjs(formValues.calibrationChargeDate)}
                            onChange={(newTime) =>
                              handleConfirm(dayjs(newTime))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{
                                  marginTop: 2, // Apply styles here
                                  width: '100%' // Optional: You can adjust the width or other styling here
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </div>
                    </>
                  )}

                  <div style={{ marginBottom: '5px' }}>
                    <TextField
                      label={
                        <FormattedMessage
                          id="grid_set_point"
                          defaultMessage="Grid Set Point (kW)"
                        />
                      }
                      name="gridSetPoint"
                      value={formValues.gridSetPoint}
                      onChange={handleChange}
                      helperText={
                        errors.gridSetPoint ? (
                          <span style={{ color: 'red' }}>
                            Please provide a valid number
                          </span>
                        ) : (
                          ''
                        )
                      }
                      fullWidth
                    />
                  </div>
                  <div style={{ marginBottom: '5px' }}>
                    <TextField
                      label={
                        <FormattedMessage
                          id="Installed_Power_DC1010"
                          defaultMessage="Installed Power DC1010 (kW)"
                        />
                      }
                      value={
                        (props.values.DcDc.SystemControl
                          .NumberOfConnectedSlaves as number) * 10
                      }
                      fullWidth
                    />
                  </div>

                  {props.values.Config.MaxBatteryDischargingCurrent && (
                    <div style={{ marginBottom: '5px' }}>
                      <TextField
                        label={
                          <FormattedMessage
                            id="Maximum_Discharge_Power"
                            defaultMessage="Maximum Discharge Power (W)"
                          />
                        }
                        value={
                          (props.values.Config
                            .MaxBatteryDischargingCurrent as number) *
                          48 *
                          (props.values.DcDc.SystemControl
                            .NumberOfConnectedSlaves as number)
                        }
                        fullWidth
                      />
                    </div>
                  )}
                </>
              )}

              {product == 3 && activeTab === 'discharge' && (
                <>
                  <div style={{ marginBottom: '5px', marginTop: '10px' }}>
                    <FormControl fullWidth sx={{ marginLeft: 1, width: 390 }}>
                      <InputLabel
                        sx={{
                          fontSize: 14,
                          backgroundColor: 'white'
                        }}
                      >
                        <FormattedMessage
                          id="calibration_discharge_state"
                          defaultMessage="Calibration Discharge State"
                        />
                      </InputLabel>
                      <Select
                        value={
                          CalibrationChargeOptions[
                            formValues.calibrationDischargeState
                          ]
                        }
                        onChange={handleSelectedCalibrationDisChargeChange}
                      >
                        {CalibrationChargeOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  {formValues.calibrationDischargeState == 1 && (
                    <div>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          label="Select Next Calibration Discharge Date"
                          value={dayjs(formValues.calibrationDischargeDate)}
                          onChange={handleConfirmDischarge}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{
                                marginTop: 2, // Apply styles here
                                width: '100%' // Optional: You can adjust the width or other styling here
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  )}

                  {formValues.calibrationDischargeState == 0 && (
                    <>
                      <div style={{ marginBottom: '5px' }}>
                        <FormControl
                          fullWidth
                          sx={{ marginLeft: 1, width: 390, marginTop: 2 }}
                        >
                          <InputLabel
                            sx={{
                              fontSize: 14,
                              backgroundColor: 'white'
                            }}
                          >
                            <FormattedMessage
                              id="calibration_charge_day"
                              defaultMessage="Calibration Discharge Day"
                            />
                          </InputLabel>
                          <Select
                            value={
                              daysInWeek[
                                formValues.calibrationDischargeDate.getDay()
                              ]
                            }
                            onChange={handleSelectedCalibrationDisChargeDay}
                          >
                            {daysInWeek.map((day) => (
                              <MenuItem key={day} value={day}>
                                {day}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>

                      <div style={{ marginBottom: '5px', marginTop: '10px' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            ampm={false}
                            label="Calibration Discharge Hour"
                            value={dayjs(formValues.calibrationDischargeDate)}
                            onChange={(newTime) =>
                              handleConfirmDischarge(dayjs(newTime))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{
                                  marginTop: 2, // Apply styles here
                                  width: '100%' // Optional: You can adjust the width or other styling here
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </div>
                    </>
                  )}
                </>
              )}

              {/*<div>*/}
              {/*  <TextField*/}
              {/*    label={*/}
              {/*      <FormattedMessage*/}
              {/*        id="Number_of_Batteries"*/}
              {/*        defaultMessage="Number of Batteries"*/}
              {/*      />*/}
              {/*    }*/}
              {/*    value={Object.keys(props.values.Battery.Devices).length}*/}
              {/*    fullWidth*/}
              {/*  />*/}
              {/*</div>*/}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: 10
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    marginLeft: '10px'
                  }}
                >
                  <FormattedMessage
                    id="applychanges"
                    defaultMessage="Apply Changes"
                  />
                </Button>
                {loading && (
                  <CircularProgress
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: '20px'
                    }}
                  />
                )}

                {updated && (
                  <Alert
                    severity="success"
                    sx={{
                      ml: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    Successfully applied configuration file
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setUpdated(false)}
                      sx={{ marginLeft: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Alert>
                )}
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      ml: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    An error has occurred
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setError(false)}
                      sx={{ marginLeft: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Alert>
                )}
              </div>
            </Box>
          </CardContent>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Configuration;
